import React, { useEffect, useMemo, useRef, useState } from 'react';
import './CustomViews.scss';
import { createMultiConditionFilter } from '../../utilities/tableFiltersv8';
import {
  sortTimeInState,
  sortDateColumn,
  sortNumericColumn,
  sortAddressColumn,
} from '../../utilities/customSortingUtils';
import SuccessfullToast from '../common/toast/SuccessfullToast';
import ErrorToast from '../common/toast/ErrorToast';
import { ALLY_CONFIGURATIONS } from '../../services/configuration/config';
import { showSuccessToast, showWarningAlert } from '../../components/toast/toastBridge';
import VirtualizationDataTable from './VirtualizationDataTable';
import GroupStatusCell from '../group-status/GroupStatusCell';
import { DefaultColumnFilterTanstackV8 } from '../../components/data-tables/helpers/DefaultColumnFilterTanstackV8';
import { formatDate } from '../../utilities/dateHelpers';

interface CustomTableData {
  Id: number;
  [key: string]: any;
}

interface CustomTableProps {
  columns: Array<any>;
  data: CustomTableData[];
  rawData: object;
  handleAssetDetailsRowClick: (row: any, index: number) => void;
  groupName: string | undefined;
  searchTerm?: string;
  applyTrigger?: boolean;
  isResetClicked?: boolean;
  selectedAssetIds?: string[];
  onSelectedAssetIdsChange?: (selectedAssetIds: string[]) => void;
}

const CustomViewDataTable: React.FC<CustomTableProps> = ({
  columns,
  data,
  rawData,
  handleAssetDetailsRowClick,
  groupName,
  searchTerm = '',
  applyTrigger,
  isResetClicked,
  selectedAssetIds: externalSelectedAssetIds,
  onSelectedAssetIdsChange,
}) => {
  const [saveConfigResponse] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({});
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>(externalSelectedAssetIds || []);
  const [listHeight, setListHeight] = useState(500);
  const MAX_WELLS_SELECTABLE = ALLY_CONFIGURATIONS.MAX_WELLS_SELECTABLE;

  // Calculate and set the dynamic height based on viewport
  useEffect(() => {
    const calculateHeight = () => {
      const viewportHeight = window.innerHeight;
      const otherUIElementsHeight = 403;
      const calculatedHeight = Math.max(200, viewportHeight - otherUIElementsHeight);
      setListHeight(calculatedHeight);
    };

    // Calculate on mount and window resize
    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Sync external selectedAssetIds changes
  useEffect(() => {
    if (externalSelectedAssetIds) {
      setSelectedAssetIds(externalSelectedAssetIds);
      // Also sync selectedRowIds based on asset IDs
      const newSelectedRowIds: Record<string, boolean> = {};
      externalSelectedAssetIds.forEach((assetId) => {
        newSelectedRowIds[assetId] = true;
      });
      setSelectedRowIds(newSelectedRowIds);
    }
  }, [externalSelectedAssetIds]);

  const toggleAllRowsSelected = (table: any) => {
    const alreadySelectedCount = Object.keys(selectedRowIds).length;
    const allAssetIds = table
      .getRowModel()
      .rows.slice(0, MAX_WELLS_SELECTABLE)
      .map((row: any) => row.original.Assetguid || row.original.assetGuid || row.original.AssetGuid)
      .filter(Boolean);

    const selectedFromCurrentData = allAssetIds.filter((assetId: any) => selectedRowIds[assetId]);
    const areAllSelected =
      allAssetIds.length > 0 && selectedFromCurrentData.length === Math.min(allAssetIds.length, MAX_WELLS_SELECTABLE);

    if (areAllSelected) {
      const updatedSelectedRowIds = { ...selectedRowIds };
      selectedFromCurrentData.forEach((assetId: any) => delete updatedSelectedRowIds[assetId]);

      setSelectedRowIds(updatedSelectedRowIds);
      const remainingSelected = selectedAssetIds.filter((id: any) => !selectedFromCurrentData.includes(id));
      setSelectedAssetIds(remainingSelected);
      onSelectedAssetIdsChange?.(remainingSelected);
      return;
    }

    if (alreadySelectedCount >= MAX_WELLS_SELECTABLE) {
      showWarningAlert?.(`You can select up to ${MAX_WELLS_SELECTABLE} wells only.`);
      return;
    }

    const assetsToSelect = allAssetIds
      .filter((assetId: any) => !selectedRowIds[assetId])
      .slice(0, MAX_WELLS_SELECTABLE - alreadySelectedCount);

    if (assetsToSelect.length > 0) {
      setSelectedRowIds((prev: any) => ({
        ...prev,
        ...Object.fromEntries(assetsToSelect.map((id: any) => [id, true])),
      }));
      const newSelected = [...selectedAssetIds, ...assetsToSelect];
      setSelectedAssetIds(newSelected);
      onSelectedAssetIdsChange?.(newSelected);
      showSuccessToast(
        `${assetsToSelect.length} wells have been added`,
        `A maximum of ${MAX_WELLS_SELECTABLE} wells can be selected to perform action`,
      );
    }
  };

  const toggleRowSelected = (assetId: string) => {
    if (!assetId) return; // Guard against undefined assetId

    const selectedCount = Object.keys(selectedRowIds).length;
    if (!selectedRowIds[assetId] && selectedCount >= MAX_WELLS_SELECTABLE) {
      showWarningAlert?.(`You can select up to ${MAX_WELLS_SELECTABLE} wells only.`);
      return;
    }
    setSelectedRowIds((prev) => {
      const updated = { ...prev };
      if (updated[assetId]) {
        delete updated[assetId];
      } else {
        updated[assetId] = true;
      }

      // Update selectedAssetIds based on the new selection
      const newSelectedAssetIds = updated[assetId]
        ? [...selectedAssetIds, assetId]
        : selectedAssetIds.filter((id) => id !== assetId);
      setSelectedAssetIds(newSelectedAssetIds);
      onSelectedAssetIdsChange?.(newSelectedAssetIds);

      return updated;
    });
  };

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  const handleFilterChange = React.useCallback((columnId: string, value: unknown, table?: any) => {
    const next = (currentFiltersRef.current || []).filter((f) => f.columnId !== columnId);

    // Decide whether to keep this filter: keep only if it contains conditions or has value
    const shouldKeep =
      value && typeof value === 'object' && Array.isArray((value as any).conditions)
        ? (value as any).conditions.length > 0
        : !!value;

    if (shouldKeep) {
      next.push({ columnId, filterValue: value });
    }

    // update state + ref
    setCurrentFilters(next);
    currentFiltersRef.current = next;

    // If the TanStack table instance was passed, sync its column filters (optional)
    if (table && typeof table.setColumnFilters === 'function') {
      const payload = next.map((f) => ({ id: f.columnId, value: f.filterValue }));
      table.setColumnFilters(payload);
    }
  }, []);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef, false, rawData);

  // Add filter property to columns
  const allColumns = useMemo(() => {
    if (!columns || columns.length === 0) return [];

    const topMaxRows = data.slice(0, MAX_WELLS_SELECTABLE);
    const allSelected =
      topMaxRows.length > 0 &&
      topMaxRows.every((row) => {
        const assetId = row.Assetguid || row.assetGuid || row.AssetGuid;
        return assetId && selectedRowIds[assetId];
      });
    const someSelected = topMaxRows.some((row) => {
      const assetId = row.Assetguid || row.assetGuid || row.AssetGuid;
      return assetId && selectedRowIds[assetId];
    });

    const checkboxColumn = {
      id: 'selection',
      accessorKey: 'selection',
      Header: ({ table }: any) => (
        <input
          type='checkbox'
          checked={allSelected}
          disabled={data.length === 0}
          ref={(el) => {
            if (el) el.indeterminate = !allSelected && someSelected;
          }}
          onChange={() => toggleAllRowsSelected(table)}
          className='card-table-checkbox mx-2'
          title={
            data.length === 0
              ? 'No cards available to select'
              : someSelected && !allSelected
                ? 'Unselect all cards'
                : allSelected
                  ? 'Unselect all cards'
                  : `Select up to ${MAX_WELLS_SELECTABLE} cards`
          }
        />
      ),
      cell: ({ row }: { row: any }) => {
        const assetId = row.original.Assetguid || row.original.assetGuid || row.original.AssetGuid;
        if (!assetId) return null; // Don't render checkbox if no assetId
        const isChecked = !!selectedRowIds[assetId];
        const onRowSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          toggleRowSelected(assetId);
          row.getToggleSelectedHandler()(e);
        };

        return (
          <input
            type='checkbox'
            checked={isChecked}
            onChange={onRowSelectChange}
            className='card-table-checkbox mx-2'
          />
        );
      },
      minSize: 0,
      maxSize: 50,
      enableColumnFilter: false,
      enableSorting: false,
      widthMode: 'fixed',
      noGrow: true,
      size: 40,
    };

    const filteredCols = columns
      .filter((col: any) => {
        if (typeof col.accessor === 'string' && col.accessor === 'Assetguid') return false;
        if (typeof col.Header === 'string' && col.Header.toLowerCase() === 'assetguid') return false;
        return true;
      })
      .map((col: any) => {
        if (col.Header === 'Well') {
          return {
            ...col,
            enablePinning: true,
            pinning: 'left',
            noGrow: true,
            size: 220,
            minSize: 220,
            maxSize: 220,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }
        if (col.Header === 'Run status') {
          return {
            ...col,
            size: 170,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }

        if (col.Header === 'Comms status') {
          return {
            ...col,
            size: 170,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }

        if (col.Header === 'Alarms') {
          return {
            ...col,
            size: 170,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }

        if (col.Header === 'Host alarms') {
          return {
            ...col,
            size: 150,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }

        if (col.Header === 'Comm') {
          return {
            ...col,
            size: 170,
            widthMode: 'fixed',
            cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
            filterFn: multiConditionFilter,
          };
        }

        if (typeof col.Header === 'string') {
          if (col.Header.toLowerCase() === 'last good scan') {
            return {
              ...col,
              id: col.id ? `${col.id}_date` : 'lastGoodScan_date',
              size: 160,
              widthMode: 'fixed',
              filterFn: multiConditionFilter,
              sortingFn: sortDateColumn,
              cell: ({ cell }: { cell: any }) => (
                <span>{formatDate(cell.getValue(), true)}</span>
              ),
            };
          }
          if (col.Header.toLowerCase() == 'time in state') {
            return {
              ...col,
              filterFn: multiConditionFilter,
              sortingFn: sortTimeInState,
            };
          }
          if (col.Header.toLowerCase() == 'portid' || col.Header.toLowerCase() == 'port id') {
            return {
              ...col,
              filterFn: multiConditionFilter,
              sortingFn: sortNumericColumn,
            };
          }
          if (col.Header.toLowerCase() == 'address') {
            return {
              ...col,
              filterFn: multiConditionFilter,
              sortingFn: sortAddressColumn,
            };
          }
        }
        if (col.Header.toLowerCase() == 'lastgoodscantime') {
          return {
            ...col,
            filterFn: multiConditionFilter,
            sortingFn: sortTimeInState,
            size: 180,
            widthMode: 'fixed',

            cell: ({ cell }: { cell: any }) => {
              const wellCell = cell.row.getVisibleCells().find((cell: any) => cell.column.columnDef.header === 'Well');
              const wellName = wellCell ? wellCell.getValue() : undefined;
              const rawDataAny = rawData as any;
              const rowRawData = rawDataAny?.find?.((row: any) => row?.Well?.value === wellName) ?? {};
              const cellDetails = rowRawData?.[cell.column.id] ?? {};
              return <span>{cellDetails.value}</span>;
            },
          };
        }

        return {
          ...col,
          filterFn: multiConditionFilter,
          cell: ({ cell }: { cell: any }) => RenderCell(cell, groupName ?? ''),
        };
      });

    return [checkboxColumn, ...filteredCols];
  }, [columns, selectedRowIds, data, selectedAssetIds]);

  const acessorKeyColumnMap = useMemo(
    () =>
      allColumns.map((col: any) => ({
        ...col,
        accessorKey: col.accessor,
        meta: {
          Filter: DefaultColumnFilterTanstackV8, // custom filter for this column
          currentFiltersRef,
        },
      })),
    [allColumns],
  );

  const [tableColumns, setTableColumns] = useState<any[]>(acessorKeyColumnMap);

  useEffect(() => {
    setTableColumns(acessorKeyColumnMap);
  }, [acessorKeyColumnMap, applyTrigger, isResetClicked]);

  const defaultColumn = useMemo(
    () => ({
      filterFn: multiConditionFilter,
      enableColumnFilter: true,
      maxSize: 290,
    }),
    [],
  );

  const RenderCell = (cell: any, groupName: string) =>
    useMemo(() => {
      const assetGuid = cell.row.original.Assetguid || cell.row.original.assetGuid || cell.row.original.AssetGuid;
      const href = `/asset/status/${assetGuid}/${groupName}`;
      return <GroupStatusCell rawData={rawData} index={cell.index} cell={cell} href={href} customViews={true} />;
    }, [handleAssetDetailsRowClick]);

  return (
    <>
      <section className='table-container-wrapper custom-view-data-table'>
        <SuccessfullToast
          isShowEnabled={saveConfigResponse}
          message={successMessage}
          callback={() => setSuccessMessage('')}
        />
        <ErrorToast isShowEnabled={!!errorMessage} message={errorMessage} callback={() => setErrorMessage('')} />
        <VirtualizationDataTable
          columns={tableColumns}
          data={data}
          tableHeight={listHeight}
          defaultColumn={defaultColumn}
          currentFilters={currentFilters}
          currentFiltersRef={currentFiltersRef}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          pinColumn={['Well']}
        />
      </section>
    </>
  );
};

export default CustomViewDataTable;
